import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import Cookies from "js-cookie";
import { jwtDecode } from "jwt-decode";
import Navbar from "../Dashboard/Navbar";
import Sidebar from "../Dashboard/Sidebar";
import { API_ENDPOINTS, API_URL } from "../../config";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import listPlugin from "@fullcalendar/list";
import interactionPlugin from "@fullcalendar/interaction";
import "./calendar.css";
import { useAuth } from "../../providers/AuthContext";

const Calendar = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [viewingAllAccounts, setViewingAllAccounts] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [userId, setUserId] = useState(null);
  const [calendarView, setCalendarView] = useState("month"); // week, month, agenda
  const [events, setEvents] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [showEventModal, setShowEventModal] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [lastFetchTime, setLastFetchTime] = useState(0);
  const [isFetching, setIsFetching] = useState(false);
  const calendarRef = React.useRef(null);

  // Debounce timer ref
  const debounceTimerRef = React.useRef(null);

  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const isInitialMount = React.useRef(true);

  // Function to debounce API calls
  const debounceFetch = (fn, delay = 1000) => {
    return (...args) => {
      // Clear any existing timer
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      // Set a new timer
      debounceTimerRef.current = setTimeout(() => {
        fn(...args);
      }, delay);
    };
  };

  // Debounced version of fetchCalendarEvents
  const debouncedFetchEvents = React.useCallback(
    debounceFetch((accountId) => {
      if (accountId === "all") {
        fetchCalendarEventsFromAllAccounts(accounts);
      } else {
        fetchCalendarEvents(accountId);
      }
    }),
    [accounts] // Add accounts as dependency
  );

  // Map our view modes to FullCalendar view types
  const viewModes = {
    week: "timeGridWeek",
    month: "dayGridMonth",
    agenda: "listWeek",
  };

  useEffect(() => {
    const fetchUserInfo = async () => {
      try {
        const token = Cookies.get("access_token");
        if (!token) {
          navigate("/");
          return;
        }

        const response = await axios.get(`${API_URL}/auth/me`);
        const user = response.data;
        setUserId(user.id);

        // Set accounts from user data instead of making separate call
        const userAccounts = user.accounts || [];
        setAccounts(userAccounts);

        // Check if user was previously viewing all accounts
        const wasViewingAll =
          localStorage.getItem("viewing_all_accounts") === "true";
        setViewingAllAccounts(wasViewingAll);

        if (wasViewingAll) {
          // Set a special "All Accounts" view without making the request
          setSelectedAccount({
            id: "all",
            email: "All Accounts",
            provider: "all",
          });

          // Fetch events from all accounts without the /accounts/all request
          if (!isFetching && userAccounts.length > 0) {
            fetchCalendarEventsFromAllAccounts(userAccounts);
          }
        } else {
          // Get saved account or primary account
          const savedAccountId = localStorage.getItem("current_account_id");
          const primaryAccount =
            userAccounts.find((acc) => acc.id === savedAccountId) ||
            userAccounts[0];

          if (primaryAccount) {
            setSelectedAccount(primaryAccount);
            if (!isFetching) {
              fetchCalendarEvents(primaryAccount.id);
            }
          } else {
            setLoading(false);
          }
        }
      } catch (error) {
        console.error("Error fetching user info:", error);
        navigate("/");
      }
    };

    // Only fetch on initial mount
    if (isInitialMount.current) {
      isInitialMount.current = false;
      fetchUserInfo();
    }
  }, [navigate]);

  const fetchCalendarEventsFromAllAccounts = async (accountsList) => {
    try {
      if (!accountsList || accountsList.length === 0 || !user) {
        console.error(
          "No accounts provided to fetchCalendarEventsFromAllAccounts"
        );
        setError(
          "No calendar accounts available. Please connect an account to view calendar."
        );
        setLoading(false);
        setIsFetching(false);
        return;
      }

      // Prevent duplicate fetches within a short time period
      const now = Date.now();
      if (now - lastFetchTime < 5000 && isFetching) {
        console.log(
          "Skipping fetch, too soon after last fetch or already fetching"
        );
        return;
      }

      setLastFetchTime(now);
      setIsFetching(true);
      setLoading(true);
      setError(null); // Clear any previous errors

      const token = Cookies.get("access_token");

      const config = {
        withCredentials: true,
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: token ? `Bearer ${token}` : undefined,
        },
      };

      console.log(
        `Fetching calendar events for all accounts (${accountsList.length} accounts)`
      );

      // Calculate date range - fetch events for 3 months around current date
      const nowDate = new Date(selectedDate);
      const startDate = new Date(nowDate);
      startDate.setMonth(nowDate.getMonth() - 1); // 1 month before

      const endDate = new Date(nowDate);
      endDate.setMonth(nowDate.getMonth() + 2); // 2 months after

      // Format for API: YYYY-MM-DD
      const formatApiDate = (date) => {
        return date.toISOString().split("T")[0];
      };

      const formattedStartDate = formatApiDate(startDate);
      const formattedEndDate = formatApiDate(endDate);

      // Fetch events from all accounts in parallel - use individual account endpoints
      const fetchPromises = accountsList.map(() => {
        // Use the individual account endpoint instead of the 'all' endpoint
        const endpoint = `${API_URL}/calendar/${user.id}/accounts/all/events?start_date=${formattedStartDate}&end_date=${formattedEndDate}`;

        console.log(
          `Fetching from account ${account.email} (${account.provider}): ${endpoint}`
        );

        return axios
          .get(endpoint, config)
          .then((response) => {
            let accountEvents = [];

            if (response.data && Array.isArray(response.data.events)) {
              accountEvents = response.data.events;
            } else if (Array.isArray(response.data)) {
              accountEvents = response.data;
            } else if (response.data && typeof response.data === "object") {
              // Check if any property contains an array that might be events
              for (const key in response.data) {
                if (Array.isArray(response.data[key])) {
                  accountEvents = response.data[key];
                  break;
                }
              }
            }

            // Add account information to each event
            return accountEvents.map((event) => ({
              ...event,
              accountId: account.id,
              accountEmail: account.email,
              accountProvider: account.provider,
            }));
          })
          .catch((error) => {
            console.error(
              `Error fetching events from account ${account.email}:`,
              error
            );
            return []; // Return empty array on error to not block other accounts
          });
      });

      // Combine results from all accounts
      const results = await Promise.all(fetchPromises);
      const allEvents = results.flat();

      console.log(
        `Total events combined from all accounts: ${allEvents.length}`
      );

      // Format events for FullCalendar
      const formattedEvents = formatEvents(allEvents);

      setEvents(formattedEvents);
    } catch (error) {
      console.error("Error fetching calendar events from all accounts:", error);
      setError("Failed to load calendar events. Please try again later.");
      setEvents([]);
    } finally {
      setLoading(false);
      setIsFetching(false);
    }
  };

  const fetchCalendarEvents = async (accountId) => {
    try {
      if (!accountId || !user) {
        console.error("No account ID provided to fetchCalendarEvents");
        setLoading(false);
        setIsFetching(false);
        return;
      }

      // Prevent duplicate fetches within a short time period
      const now = Date.now();
      if (now - lastFetchTime < 5000 && isFetching) {
        console.log(
          "Skipping fetch, too soon after last fetch or already fetching"
        );
        return;
      }

      setLastFetchTime(now);
      setIsFetching(true);
      setLoading(true);
      setError(null); // Clear any previous errors

      const token = Cookies.get("access_token");

      const config = {
        withCredentials: true,
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: token ? `Bearer ${token}` : undefined,
        },
      };

      console.log(`Fetching calendar events for account ID: ${accountId}`);

      // Calculate date range - fetch events for 3 months around current date
      const nowDate = new Date(selectedDate);
      const startDate = new Date(nowDate);
      startDate.setMonth(nowDate.getMonth() - 1); // 1 month before

      const endDate = new Date(nowDate);
      endDate.setMonth(nowDate.getMonth() + 2); // 2 months after

      // Format for API: YYYY-MM-DD
      const formatApiDate = (date) => {
        return date.toISOString().split("T")[0];
      };

      const formattedStartDate = formatApiDate(startDate);
      const formattedEndDate = formatApiDate(endDate);

      // Try both endpoints if necessary
      let response;
      let endpoint;

      try {
        // First try the events endpoint with date range
        console.log("Calendar Events called:");
        endpoint = API_ENDPOINTS.CALENDAR_EVENTS(
          userId,
          accountId,
          formattedStartDate,
          formattedEndDate
        );

        console.log(`Using events endpoint: ${endpoint}`);
        response = await axios.get(endpoint, config);
      } catch (firstError) {
        console.error("Error with events endpoint:", firstError);

        // Fall back to the account endpoint
        try {
          endpoint = API_ENDPOINTS.CALENDAR_ACCOUNT(user.id);
          console.log(`Trying fallback endpoint: ${endpoint}`);
          response = await axios.get(endpoint, config);
        } catch (secondError) {
          console.error("Error with fallback endpoint:", secondError);
          throw secondError; // Re-throw to be caught by the outer catch
        }
      }

      console.log("Calendar API response:", response);

      let fetchedEvents = [];

      if (response.data && Array.isArray(response.data.events)) {
        fetchedEvents = response.data.events;
        console.log(
          `Found ${fetchedEvents.length} events in response.data.events`
        );
      } else if (Array.isArray(response.data)) {
        fetchedEvents = response.data;
        console.log(
          `Found ${fetchedEvents.length} events in response.data array`
        );
      } else {
        console.warn("Unexpected response format:", response.data);
        // Try to handle different data formats
        if (response.data && typeof response.data === "object") {
          // Check if any property contains an array that might be events
          for (const key in response.data) {
            if (Array.isArray(response.data[key])) {
              console.log(
                `Found possible events array in response.data.${key}`
              );
              fetchedEvents = response.data[key];
              break;
            }
          }
        }
      }

      // Format events for FullCalendar
      console.log("Raw events before formatting:", fetchedEvents);
      const formattedEvents = formatEvents(fetchedEvents);
      console.log("Formatted events for FullCalendar:", formattedEvents);

      setEvents(formattedEvents);
    } catch (error) {
      console.error("Error fetching calendar events:", error);

      // Enhanced error logging
      if (error.response) {
        // The request was made and the server responded with a status code
        // that falls out of the range of 2xx
        console.error("Error response data:", error.response.data);
        console.error("Error response status:", error.response.status);
        console.error("Error response headers:", error.response.headers);

        if (error.response.status === 401) {
          setError("Session expired. Please log in again.");
          // Redirect to login page after a short delay
          setTimeout(() => navigate("/"), 3000);
        } else {
          setError(
            `Failed to load calendar events. Server returned: ${error.response.status} ${error.response.statusText}`
          );
        }
      } else if (error.request) {
        // The request was made but no response was received
        console.error("Error request:", error.request);
        setError(
          "Failed to load calendar events. No response received from server. Check your connection."
        );
      } else {
        // Something happened in setting up the request that triggered an Error
        console.error("Error message:", error.message);
        setError(`Failed to load calendar events: ${error.message}`);
      }

      // Set empty events array to prevent further errors
      setEvents([]);
    } finally {
      // Always make sure to reset loading and fetching states
      setLoading(false);
      setIsFetching(false);
    }
  };

  // Convert events to FullCalendar format
  const formatEvents = (calendarEvents) => {
    if (!calendarEvents || !Array.isArray(calendarEvents)) {
      console.warn("formatEvents called with invalid data:", calendarEvents);
      return [];
    }

    try {
      return calendarEvents
        .map((event) => {
          try {
            if (!event) {
              console.warn("Null or undefined event in calendarEvents array");
              return null;
            }

            // Handle both date-only and datetime events
            let startDate = null;
            let endDate = null;
            let allDay = false;

            try {
              startDate = event.start?.dateTime || event.start?.date;
              endDate = event.end?.dateTime || event.end?.date;
              allDay = !event.start?.dateTime;

              // Validate dates
              if (startDate) {
                // Try parsing the date to validate it
                new Date(startDate);
              }
              if (endDate) {
                // Try parsing the date to validate it
                new Date(endDate);
              }
            } catch (dateError) {
              console.error("Error parsing event dates:", dateError, event);
              // Use fallbacks
              startDate = null;
              endDate = null;
              allDay = true;
            }

            // Determine event type and color based on title or content
            let eventType = "default";
            let backgroundColor = "#f9fafb";
            let borderColor = "#e5e7eb";
            let textColor = "#374151";
            let eventIcon = "";

            const title = (event.summary || event.subject || "").toLowerCase();

            if (
              title.includes("meet") ||
              title.includes("meeting") ||
              title.includes("discussion")
            ) {
              eventType = "meeting";
              borderColor = "#4f46e5";
              backgroundColor = "#eef2ff";
              textColor = "#4338ca";
              eventIcon = '<i class="fas fa-users mr-1"></i>';
            } else if (title.includes("call") || title.includes("chat")) {
              eventType = "call";
              borderColor = "#8b5cf6";
              backgroundColor = "#f5f3ff";
              textColor = "#7c3aed";
              eventIcon = '<i class="fas fa-phone mr-1"></i>';
            } else if (
              title.includes("lunch") ||
              title.includes("dinner") ||
              title.includes("breakfast")
            ) {
              eventType = "meal";
              borderColor = "#d97706";
              backgroundColor = "#fffbeb";
              textColor = "#b45309";
              eventIcon = '<i class="fas fa-utensils mr-1"></i>';
            } else if (
              title.includes("birthday") ||
              title.includes("celebration") ||
              title.includes("party")
            ) {
              eventType = "celebration";
              borderColor = "#ec4899";
              backgroundColor = "#fdf2f8";
              textColor = "#db2777";
              eventIcon = '<i class="fas fa-birthday-cake mr-1"></i>';
            } else if (
              title.includes("deadline") ||
              title.includes("due") ||
              title.includes("submit")
            ) {
              eventType = "deadline";
              borderColor = "#dc2626";
              backgroundColor = "#fee2e2";
              textColor = "#b91c1c";
              eventIcon = '<i class="fas fa-exclamation-circle mr-1"></i>';
            } else if (
              title.includes("travel") ||
              title.includes("flight") ||
              title.includes("trip")
            ) {
              eventType = "travel";
              borderColor = "#0ea5e9";
              backgroundColor = "#e0f2fe";
              textColor = "#0284c7";
              eventIcon = '<i class="fas fa-plane mr-1"></i>';
            } else if (
              event.conferenceData?.link ||
              event.hangoutLink ||
              (event.location && event.location.includes("meet.google.com"))
            ) {
              eventType = "virtual";
              borderColor = "#059669";
              backgroundColor = "#ecfdf5";
              textColor = "#047857";
              eventIcon = '<i class="fas fa-video mr-1"></i>';
            }

            // Check if the event has attendees to suggest it's important
            const hasAttendees = event.attendees && event.attendees.length > 2;

            // Add provider-specific className for styling when in all-accounts view
            let providerClassName = "";
            if (viewingAllAccounts && event.accountProvider) {
              providerClassName = `fc-event-${event.accountProvider.toLowerCase()}`;
            }

            // Format for FullCalendar
            return {
              id:
                event.id || `event-${Math.random().toString(36).substr(2, 9)}`,
              title: event.summary || event.subject || "Untitled Event",
              start: startDate,
              end: endDate,
              allDay: allDay,
              className: `${eventType} ${providerClassName}`,
              description: event.description || event.bodyPreview || "",
              location: event.location || "",
              url: event.htmlLink || null,
              backgroundColor: backgroundColor,
              borderColor: borderColor,
              textColor: textColor,
              extendedProps: {
                attendees: event.attendees || [],
                conferenceData: event.conferenceData || null,
                creator: event.creator || null,
                organizer: event.organizer || null,
                location: event.location || "",
                description: event.description || event.bodyPreview || "",
                icon: eventIcon,
                eventType: eventType,
                hasAttendees: hasAttendees,
                originalEvent: event,
                accountId: event.accountId,
                accountEmail: event.accountEmail,
                accountProvider: event.accountProvider,
              },
            };
          } catch (eventError) {
            console.error(
              "Error formatting individual event:",
              eventError,
              event
            );
            // Return a minimal valid event to prevent entire array from failing
            return {
              id: `error-${Math.random().toString(36).substr(2, 9)}`,
              title: "Error loading event",
              start: new Date(),
              allDay: true,
              color: "#ff0000",
            };
          }
        })
        .filter((event) => event !== null); // Remove any null events
    } catch (error) {
      console.error("Error in formatEvents:", error);
      return [];
    }
  };

  // Map color IDs to hex colors
  const getEventColor = (colorId) => {
    if (!colorId) return undefined;

    const colors = {
      1: "#7986cb", // Lavender
      2: "#33b679", // Sage
      3: "#8e24aa", // Grape
      4: "#e67c73", // Flamingo
      5: "#f6bf26", // Banana
      6: "#f4511e", // Tangerine
      7: "#039be5", // Peacock
      8: "#616161", // Graphite
      9: "#3f51b5", // Blueberry
      10: "#0b8043", // Basil
      11: "#d50000", // Tomato
    };
    return colors[colorId] || "#039be5"; // Default to Peacock blue
  };

  const handleAccountChange = (account) => {
    // Special handling for viewing all accounts
    if (account.id === "all") {
      setSelectedAccount(account);
      setViewingAllAccounts(true);
      localStorage.setItem("viewing_all_accounts", "true");

      // Fetch events from all accounts without making the /accounts/all request
      fetchCalendarEventsFromAllAccounts(accounts);
    } else {
      setSelectedAccount(account);
      setViewingAllAccounts(false);
      localStorage.setItem("current_account_id", account.id);
      localStorage.setItem("viewing_all_accounts", "false");

      // Use the non-debounced version for direct user actions
      fetchCalendarEvents(selectedAccount?.id);
    }
  };

  // Handler for date navigation - fetch new events when calendar view changes
  const handleDateChange = (info) => {
    console.log("Calendar view date change:", info);

    // Update the selectedDate state to reflect the current view's start date
    setSelectedDate(info.view.currentStart);

    // Use the debounced fetch to avoid multiple API calls
    if (viewingAllAccounts) {
      debouncedFetchEvents("all"); // Special signal for all accounts
    } else if (selectedAccount && selectedAccount.id) {
      debouncedFetchEvents(selectedAccount.id);
    }
  };

  // Handler for clicking on an event
  const handleEventClick = (clickInfo) => {
    // Set the selected event
    setSelectedEvent(clickInfo.event);
    setShowEventModal(true);

    // Prevent default behavior if there's a URL
    if (clickInfo.event.url) {
      clickInfo.jsEvent.preventDefault();
    }
  };

  // Close the event modal
  const closeEventModal = () => {
    setShowEventModal(false);
    setSelectedEvent(null);
  };

  const openExternalCalendar = () => {
    if (!selectedAccount) return;

    if (viewingAllAccounts) {
      // For "all accounts" view, just open the default calendar of the first account
      if (accounts.length > 0) {
        const firstAccount = accounts[0];
        if (firstAccount.provider === "google") {
          window.open(
            `https://calendar.google.com/calendar/u/${encodeURIComponent(
              firstAccount.email
            )}/r`,
            "_blank"
          );
        } else if (firstAccount.provider === "outlook") {
          window.open("https://outlook.office.com/calendar/", "_blank");
        }
      }
    } else if (selectedAccount.provider === "google") {
      window.open(
        `https://calendar.google.com/calendar/u/${encodeURIComponent(
          selectedAccount.email
        )}/r`,
        "_blank"
      );
    } else if (selectedAccount.provider === "outlook") {
      window.open("https://outlook.office.com/calendar/", "_blank");
    }
  };

  // Render the event modal
  const renderEventModal = () => {
    if (!showEventModal || !selectedEvent) return null;

    const event = selectedEvent;
    const extendedProps = event.extendedProps || {};
    const accountEmail = extendedProps.accountEmail;
    const accountProvider = extendedProps.accountProvider;

    // Extract meeting link from different possible sources
    const meetingLink =
      extendedProps.conferenceData?.link ||
      extendedProps.conferenceData?.joinUrl ||
      extendedProps.originalEvent?.hangoutLink ||
      extendedProps.originalEvent?.onlineMeeting?.joinUrl ||
      "";

    // Determine meeting provider based on link or original data
    let meetingProvider = "";
    let meetingIcon = "";

    if (meetingLink.includes("teams.microsoft.com")) {
      meetingProvider = "Microsoft Teams";
      meetingIcon = `
        <svg class="w-4 h-4 mr-1" fill="#5059C9" viewBox="0 0 24 24">
          <path d="M19.2 6H14c-.9 0-1.7.4-2.2 1.1l-4.2 5.3c-.2.3-.6.5-1 .5H4v1h2.6c.9 0 1.7-.4 2.2-1.1l4.2-5.3c.2-.3.6-.5 1-.5h5.2c.6 0 1 .4 1 1v10.2c0 .6-.4 1-1 1H4v1h15.2c1.1 0 2-.9 2-2V8c0-1.1-.9-2-2-2z"/>
        </svg>
      `;
    } else if (meetingLink.includes("meet.google.com")) {
      meetingProvider = "Google Meet";
      meetingIcon = `
        <svg class="w-4 h-4 mr-1" viewBox="0 0 24 24">
          <path fill="#00AC47" d="M7.463 14.688l-2.53-1.51L1 15.924V19h3.776a2 2 0 001.353-.526l1.334-1.334V14.688z" />
          <path fill="#4285F4" d="M23 11a2 2 0 00-2-2h-8a2 2 0 00-2 2v7.5a2 2 0 002 2h8a2 2 0 002-2V11z" />
          <path fill="#EA4335" d="M7.463 11.512V9.077L4.934 7.53 1 10.276v3.075l3.934-2.747 2.53 1.51-.001-.602z" />
          <path fill="#00832D" d="M7.463 9.077v2.437c0 .233-.03.462-.084.683a2 2 0 01-.527 2.492L5.33 16.21a2 2 0 01-1.556.79H1V10.276l3.934-2.747 2.53 1.547z" />
        </svg>
      `;
    } else if (meetingLink.includes("zoom.us")) {
      meetingProvider = "Zoom";
      meetingIcon = `
        <svg class="w-4 h-4 mr-1" viewBox="0 0 24 24" fill="#2D8CFF">
          <path d="M24 12c0 6.627-5.373 12-12 12S0 18.627 0 12 5.373 0 12 0s12 5.373 12 12zm-6.508-3.941c-.979-.816-2.279-1.23-3.855-1.23h-5.83c-.4 0-.726.325-.726.725v10.89c0 .4.326.726.725.726h5.83c1.576 0 2.88-.415 3.862-1.232a5.602 5.602 0 001.922-2.74 5.888 5.888 0 00.312-1.858c0-1.736-.6-3.14-1.78-4.15a.5.5 0 01-.46-.13z"/>
          <path d="M17.7 12c0 .9-.3 1.5-.8 2.1-.6.5-1.3.8-2.2.8h-4.5v-5.7h4.5c.9 0 1.6.2 2.2.8.5.5.8 1.2.8 2zM6.9 9.5v6.2L4 14.1v1.6l3.7 2.1V8.9L4 7v1.6l2.9 1z" fill="#fff"/>
        </svg>
      `;
    } else if (meetingLink) {
      meetingProvider = "Online Meeting";
      meetingIcon = `
        <svg class="w-4 h-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
        </svg>
      `;
    }

    return (
      <div className="fixed top-[25%] left-[50%] z-50">
        <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-4 max-h-[90vh] overflow-y-auto">
          <div className="flex justify-between items-start mb-4">
            <h3 className="text-xl font-semibold text-gray-800">
              {event.title}
            </h3>
            <button
              onClick={closeEventModal}
              className="text-gray-500 hover:text-gray-700"
            >
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          <div className="space-y-4 text-gray-700">
            {/* Account info if viewing all accounts */}
            {viewingAllAccounts && accountEmail && accountProvider && (
              <div className="flex items-start bg-blue-50 p-3 rounded-lg">
                <div className="bg-blue-100 p-2 rounded-full mr-3">
                  {accountProvider === "google" ? (
                    <svg className="w-5 h-5" viewBox="0 0 24 24">
                      <path
                        fill="#DB4437"
                        d="M5.15 16.75L9.5 13.5L5.15 10.25L5.15 16.75Z"
                      />
                      <path
                        fill="#0F9D58"
                        d="M14.5 13.5L18.85 16.75L18.85 10.25L14.5 13.5Z"
                      />
                      <path
                        fill="#F4B400"
                        d="M14.5 13.5L9.5 13.5L5.15 16.75L18.85 16.75L14.5 13.5Z"
                      />
                      <path
                        fill="#4285F4"
                        d="M18.85 10.25L5.15 10.25L9.5 13.5L14.5 13.5L18.85 10.25Z"
                      />
                    </svg>
                  ) : accountProvider === "outlook" ? (
                    <svg className="w-5 h-5" viewBox="0 0 24 24">
                      <path
                        fill="#0078D4"
                        d="M1 5.5V18.5H9.5V24L16.5 18.5H23V5.5H1Z"
                      />
                      <path fill="#FFFFFF" d="M8 10H6V15H8V10Z" />
                      <path
                        fill="#FFFFFF"
                        d="M7 9C7.5 9 8 8.5 8 8C8 7.5 7.5 7 7 7C6.5 7 6 7.5 6 8C6 8.5 6.5 9 7 9Z"
                      />
                      <path
                        fill="#FFFFFF"
                        d="M12 15L12 10H14C15.5 10 16.5 11 16.5 12.5C16.5 14 15.5 15 14 15H12Z"
                      />
                    </svg>
                  ) : (
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                      />
                    </svg>
                  )}
                </div>
                <div>
                  <div className="font-medium">Account</div>
                  <div className="text-sm">{accountEmail}</div>
                  <div className="text-xs text-gray-500">
                    {accountProvider.charAt(0).toUpperCase() +
                      accountProvider.slice(1)}
                  </div>
                </div>
              </div>
            )}

            {/* Date and time */}
            <div className="flex items-start bg-gray-50 p-3 rounded-lg">
              <div className="bg-blue-100 p-2 rounded-full mr-3">
                <svg
                  className="w-5 h-5 text-blue-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                  />
                </svg>
              </div>
              <div>
                <div className="font-medium">
                  {event.start
                    ? event.start.toLocaleDateString([], {
                        weekday: "long",
                        month: "long",
                        day: "numeric",
                        year: "numeric",
                      })
                    : ""}
                </div>
                <div className="text-sm text-gray-500">
                  {event.allDay
                    ? "All day"
                    : `${
                        event.start
                          ? event.start.toLocaleTimeString([], {
                              hour: "numeric",
                              minute: "2-digit",
                              hour12: true,
                            })
                          : ""
                      } - 
                     ${
                       event.end
                         ? event.end.toLocaleTimeString([], {
                             hour: "numeric",
                             minute: "2-digit",
                             hour12: true,
                           })
                         : ""
                     }`}
                </div>
              </div>
            </div>

            {/* Meeting link */}
            {meetingLink && (
              <div className="flex items-start bg-indigo-50 p-3 rounded-lg">
                <div className="bg-indigo-100 p-2 rounded-full mr-3">
                  <svg
                    className="w-5 h-5 text-indigo-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
                    />
                  </svg>
                </div>
                <div className="flex-1">
                  <div className="font-medium">
                    {meetingProvider || "Join Meeting"}
                  </div>
                  <div className="mt-1">
                    <a
                      href={meetingLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                    >
                      <span dangerouslySetInnerHTML={{ __html: meetingIcon }} />
                      Join Meeting
                    </a>
                  </div>
                </div>
              </div>
            )}

            {/* Location if available */}
            {event.extendedProps.location && (
              <div className="flex items-start bg-amber-50 p-3 rounded-lg">
                <div className="bg-amber-100 p-2 rounded-full mr-3">
                  <svg
                    className="w-5 h-5 text-amber-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                    />
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                    />
                  </svg>
                </div>
                <div>
                  <div className="font-medium">Location</div>
                  <div className="text-sm">{event.extendedProps.location}</div>
                </div>
              </div>
            )}

            {/* Description if available */}
            {event.extendedProps.description && (
              <div className="bg-white border border-gray-200 p-3 rounded-lg">
                <h4 className="font-medium mb-2 text-gray-900">Description</h4>
                <div className="text-sm whitespace-pre-wrap text-gray-600">
                  {event.extendedProps.description}
                </div>
              </div>
            )}

            {/* Attendees if available */}
            {extendedProps.attendees && extendedProps.attendees.length > 0 && (
              <div className="bg-white border border-gray-200 p-3 rounded-lg">
                <h4 className="font-medium mb-2 text-gray-900">
                  Attendees ({extendedProps.attendees.length})
                </h4>
                <ul className="text-sm text-gray-600 space-y-1">
                  {extendedProps.attendees.slice(0, 5).map((attendee, idx) => (
                    <li key={idx} className="flex items-center">
                      <div className="w-6 h-6 rounded-full bg-gray-300 flex items-center justify-center mr-2 text-xs font-bold text-gray-600">
                        {(attendee.email || attendee.name || "")
                          .substring(0, 1)
                          .toUpperCase()}
                      </div>
                      {attendee.email || attendee.name || "Anonymous"}
                    </li>
                  ))}
                  {extendedProps.attendees.length > 5 && (
                    <li className="text-gray-500 italic">
                      And {extendedProps.attendees.length - 5} more...
                    </li>
                  )}
                </ul>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  // If all attempts to fetch events fail, we still want to show an empty calendar
  const getEmptyEventsIfNeeded = () => {
    // Check if we need to create a placeholder event
    if (events.length === 0 && !loading) {
      console.log("No events found, rendering empty calendar");

      // Return empty array so the calendar can still render
      return [];
    }

    return events;
  };

  // Render the FullCalendar component
  const renderFullCalendar = (calendarEvents) => {
    return (
      <FullCalendar
        ref={calendarRef}
        plugins={[dayGridPlugin, timeGridPlugin, listPlugin, interactionPlugin]}
        initialView={viewModes[calendarView] || "timeGridWeek"}
        initialDate={selectedDate}
        view={viewModes[calendarView]}
        headerToolbar={false}
        events={calendarEvents}
        eventClick={handleEventClick}
        datesSet={(dateInfo) => {
          // Update selectedDate when calendar dates change
          setSelectedDate(dateInfo.start);
        }}
        refetchResourcesOnNavigate={false}
        lazyFetching={true}
        forceEventDuration={true}
        height="auto"
        contentHeight="auto"
        eventTimeFormat={{
          hour: "2-digit",
          minute: "2-digit",
          meridiem: "short",
        }}
        navLinks={false}
        eventDisplay="block"
        nowIndicator={true}
        dayMaxEvents={true}
        selectable={true}
        weekNumbers={false}
        slotEventOverlap={false}
        businessHours={{
          daysOfWeek: [1, 2, 3, 4, 5],
          startTime: "09:00",
          endTime: "17:00",
        }}
        allDaySlot={false}
      />
    );
  };

  // Render the calendar header to use the current view's date
  const renderCalendarHeader = () => {
    if (!calendarRef.current) return null;

    const calendar = calendarRef.current.getApi();
    const currentDate = calendar ? calendar.getDate() : selectedDate;

    const dateFormatter = new Intl.DateTimeFormat("en", {
      month: "long",
      year: "numeric",
    });

    let currentCalendarTitle = "";

    if (calendarView === "week") {
      const weekStart = new Date(currentDate);
      const weekEnd = new Date(currentDate);
      weekEnd.setDate(weekEnd.getDate() + 6);

      const monthFormatter = new Intl.DateTimeFormat("en", { month: "long" });
      const yearFormatter = new Intl.DateTimeFormat("en", { year: "numeric" });
      const dayFormatter = new Intl.DateTimeFormat("en", { day: "numeric" });

      const startMonth = monthFormatter.format(weekStart);
      const endMonth = monthFormatter.format(weekEnd);
      const startYear = yearFormatter.format(weekStart);
      const endYear = yearFormatter.format(weekEnd);

      if (startMonth === endMonth && startYear === endYear) {
        currentCalendarTitle = `${startMonth} ${startYear}`;
      } else if (startYear === endYear) {
        currentCalendarTitle = `${startMonth} - ${endMonth} ${startYear}`;
      } else {
        currentCalendarTitle = `${startMonth} ${startYear} - ${endMonth} ${endYear}`;
      }
    } else if (calendarView === "agenda") {
      const weekStart = new Date(currentDate);
      const weekEnd = new Date(currentDate);
      weekEnd.setDate(weekEnd.getDate() + 6);

      const monthFormatter = new Intl.DateTimeFormat("en", { month: "long" });
      const dayFormatter = new Intl.DateTimeFormat("en", { day: "numeric" });

      currentCalendarTitle = `${monthFormatter.format(
        weekStart
      )} ${dayFormatter.format(weekStart)} - ${monthFormatter.format(
        weekEnd
      )} ${dayFormatter.format(weekEnd)}`;
    } else {
      currentCalendarTitle = dateFormatter.format(currentDate);
    }

    return (
      <div className="bg-white border-b border-gray-200 shadow-sm calendar-header">
        <div className="px-1 py-0.5 flex items-center justify-between">
          <div className="flex space-x-1 items-center">
            <button
              onClick={() => {
                if (calendarRef.current) {
                  const api = calendarRef.current.getApi();
                  api.prev();
                  setSelectedDate(api.getDate());
                }
              }}
              className="p-1 rounded-full hover:bg-gray-100 transition-colors"
              aria-label="Previous"
            >
              <svg
                className="w-2 h-2 text-gray-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 19l-7-7 7-7"
                />
              </svg>
            </button>

            <button
              onClick={() => {
                if (calendarRef.current) {
                  const api = calendarRef.current.getApi();
                  api.today();
                  setSelectedDate(api.getDate());
                }
              }}
              className="px-2 py-0.5 rounded-md text-xs bg-gray-100 hover:bg-gray-200 transition-colors"
            >
              Today
            </button>

            <button
              onClick={() => {
                if (calendarRef.current) {
                  const api = calendarRef.current.getApi();
                  api.next();
                  setSelectedDate(api.getDate());
                }
              }}
              className="p-1 rounded-full hover:bg-gray-100 transition-colors"
              aria-label="Next"
            >
              <svg
                className="w-2 h-2 text-gray-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5l7 7-7 7"
                />
              </svg>
            </button>

            <div className="flex items-center ml-2">
              <h2 className="text-xs font-semibold text-gray-800">
                {currentCalendarTitle}
              </h2>
              {viewingAllAccounts && (
                <span className="ml-1 px-1 py-0.5 text-xs bg-blue-100 text-blue-800 rounded-full font-medium">
                  All Accounts ({accounts.length})
                </span>
              )}
            </div>
          </div>

          <div className="flex space-x-1">
            <button
              onClick={() => {
                if (calendarRef.current) {
                  const api = calendarRef.current.getApi();
                  api.changeView("dayGridMonth");
                  setCalendarView("month");
                  setSelectedDate(api.getDate());
                }
              }}
              className={`px-2 py-0.5 text-xs rounded-md transition-colors ${
                calendarView === "month"
                  ? "bg-[#1F9BFF1A] text-[#1F9BFF]"
                  : "text-gray-700 hover:bg-gray-100"
              }`}
            >
              Month
            </button>
            <button
              onClick={() => {
                if (calendarRef.current) {
                  const api = calendarRef.current.getApi();
                  api.changeView("timeGridWeek");
                  setCalendarView("week");
                  setSelectedDate(api.getDate());
                }
              }}
              className={`px-2 py-0.5 text-xs rounded-md  transition-colors ${
                calendarView === "week"
                  ? "bg-[#1F9BFF1A] text-[#1F9BFF]"
                  : "text-gray-700 hover:bg-gray-100"
              }`}
            >
              Week
            </button>
            <button
              onClick={() => {
                if (calendarRef.current) {
                  const api = calendarRef.current.getApi();
                  api.changeView("listWeek");
                  setCalendarView("agenda");
                  setSelectedDate(api.getDate());
                }
              }}
              className={`px-2 py-0.5 text-xs rounded-md  transition-colors ${
                calendarView === "agenda"
                  ? "bg-[#1F9BFF1A] text-[#1F9BFF]"
                  : "text-gray-700 hover:bg-gray-100"
              }`}
            >
              Agenda
            </button>
          </div>
        </div>
      </div>
    );
  };

  // For other account types, we use the FullCalendar implementation directly in this component
  const renderDirectCalendar = () => {
    return (
      <div className="calendar-container h-full flex flex-col overflow-hidden">
        {error && (
          <div className="calendar-error mb-2 p-2 bg-red-50 border border-red-300 text-red-800 rounded-md">
            <p className="font-medium">
              <i className="fas fa-exclamation-circle mr-2"></i> Error: {error}
            </p>
            <p className="text-sm mt-1">
              Please try refreshing the page or selecting a different account.
            </p>
          </div>
        )}

        <div className="events-count-display mb-1 p-1 bg-[#1F9BFF1A] border border-[1F9BFF1A] text-[#1F9BFF] rounded-md">
          {events.length > 0 ? (
            <p className="font-bold flex justify-between items-center">
              <span>
                <i className="far fa-calendar-check mr-2 text-xs font-bold"></i>
                Found {events.length} event{events.length !== 1 ? "s" : ""} for
                the selected period
              </span>
              {viewingAllAccounts && (
                <span className="text-xs bg-blue-50 px-2 py-1 rounded-lg">
                  Showing events from {accounts.length} account
                  {accounts.length !== 1 ? "s" : ""}
                </span>
              )}
            </p>
          ) : (
            <p className="font-bold">
              <i className="far fa-calendar-times mr-2 text-xs font-bold"></i>
              No events found for the selected period
              {viewingAllAccounts && accounts.length > 0 && (
                <span className="text-xs bg-blue-50 px-2 py-1 rounded-lg ml-2">
                  Checked {accounts.length} account
                  {accounts.length !== 1 ? "s" : ""}
                </span>
              )}
            </p>
          )}
        </div>

        {renderCalendarHeader()}

        <div className="calendar-body flex-1 overflow-y-auto">
          <FullCalendar
            plugins={[
              dayGridPlugin,
              timeGridPlugin,
              listPlugin,
              interactionPlugin,
            ]}
            initialView="dayGridMonth"
            headerToolbar={false}
            dayHeaderContent={renderDayHeader}
            height="100%"
            contentHeight="100%"
            themeSystem="standard"
            events={events}
            eventClick={handleEventClick}
            eventTimeFormat={{
              hour: "2-digit",
              minute: "2-digit",
              meridiem: "short",
            }}
            eventContent={renderEventContent}
            dayMaxEvents={3}
            moreLinkClick="day"
            navLinks={true}
            ref={calendarRef}
            loading={loading}
            longPressDelay={0}
            allDaySlot={false}
            allDayText="All day"
            slotEventOverlap={false}
            stickyHeaderDates={true}
            nowIndicator={true}
            slotDuration="00:30:00"
            slotLabelInterval="01:00"
            slotLabelFormat={{
              hour: "numeric",
              minute: "2-digit",
              omitZeroMinute: true,
              hour12: true,
            }}
            scrollTime="08:00:00"
            businessHours={{
              daysOfWeek: [1, 2, 3, 4, 5],
              startTime: "09:00",
              endTime: "17:00",
            }}
          />
        </div>

        {renderEventModal()}
      </div>
    );
  };

  // Custom event rendering
  const renderEventContent = (eventInfo) => {
    const { event } = eventInfo;
    const icon = event.extendedProps.icon;
    const hasAttendees = event.extendedProps.hasAttendees;
    const accountEmail = event.extendedProps.accountEmail;
    const accountProvider = event.extendedProps.accountProvider;
    const showAccountBadge = viewingAllAccounts && accountEmail;

    return (
      <div
        className={`fc-event-content ${event.extendedProps.eventType}-event`}
      >
        {icon && (
          <div
            className="event-icon"
            dangerouslySetInnerHTML={{ __html: icon }}
          ></div>
        )}
        <div className="event-title-container">
          <div className="event-title">
            {event.title}
            {hasAttendees && <span className="ml-1 text-xs">👥</span>}
          </div>
          {!event.allDay && (
            <div className="event-time text-xs opacity-90">
              {eventInfo.timeText}
            </div>
          )}
          {showAccountBadge && (
            <div className="event-account text-xs opacity-75 mt-0 flex items-center">
              {accountProvider === "google" ? (
                <span className="inline-flex items-center">
                  <svg className="w-2 h-2 mr-1" viewBox="0 0 24 24">
                    <path
                      fill="#DB4437"
                      d="M5.15 16.75L9.5 13.5L5.15 10.25L5.15 16.75Z"
                    />
                    <path
                      fill="#0F9D58"
                      d="M14.5 13.5L18.85 16.75L18.85 10.25L14.5 13.5Z"
                    />
                    <path
                      fill="#F4B400"
                      d="M14.5 13.5L9.5 13.5L5.15 16.75L18.85 16.75L14.5 13.5Z"
                    />
                    <path
                      fill="#4285F4"
                      d="M18.85 10.25L5.15 10.25L9.5 13.5L14.5 13.5L18.85 10.25Z"
                    />
                  </svg>
                  {accountEmail.split("@")[0]}
                </span>
              ) : accountProvider === "outlook" ? (
                <span className="inline-flex items-center">
                  <svg className="w-2 h-2 mr-1" viewBox="0 0 24 24">
                    <path
                      fill="#0078D4"
                      d="M1 5.5V18.5H9.5V24L16.5 18.5H23V5.5H1Z"
                    />
                    <path fill="#FFFFFF" d="M8 10H6V15H8V10Z" />
                    <path
                      fill="#FFFFFF"
                      d="M7 9C7.5 9 8 8.5 8 8C8 7.5 7.5 7 7 7C6.5 7 6 7.5 6 8C6 8.5 6.5 9 7 9Z"
                    />
                    <path
                      fill="#FFFFFF"
                      d="M12 15L12 10H14C15.5 10 16.5 11 16.5 12.5C16.5 14 15.5 15 14 15H12Z"
                    />
                  </svg>
                  {accountEmail.split("@")[0]}
                </span>
              ) : (
                <span className="inline-flex items-center">
                  <svg className="w-2 h-2 mr-1" viewBox="0 0 24 24">
                    <path
                      fill="#718096"
                      d="M20 4H4C2.9 4 2.01 4.9 2.01 6L2 18C2 19.1 2.9 20 4 20H20C21.1 20 22 19.1 22 18V6C22 4.9 21.1 4 20 4ZM20 8L12 13L4 8V6L12 11L20 6V8Z"
                    />
                  </svg>
                  {accountEmail.split("@")[0]}
                </span>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderDayHeader = (args) => {
    const day = args.date.getDay();
    const weekdays = ["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"];
    return (
      <div className="fc-day-header text-xs">
        <span className="day-name">{weekdays[day]}</span>
      </div>
    );
  };

  const renderCalendarContent = () => {
    if (loading) {
      return (
        <div className="flex-1 flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
        </div>
      );
    }

    if (!selectedAccount) {
      return (
        <div className="flex-1 flex items-center justify-center text-gray-500">
          <div className="text-center p-8">
            <svg
              className="w-16 h-16 mx-auto text-gray-300"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1}
                d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
              />
            </svg>
            <h3 className="mt-4 text-xs font-medium">
              No calendar account selected
            </h3>
            <p className="mt-2 text-sm">
              Select an account from the dropdown above to view your calendar.
            </p>
          </div>
        </div>
      );
    }

    // Always show our custom calendar implementation
    // The specialized components might be causing the infinite loop
    return renderDirectCalendar();
  };

  // Handle changing views (week/month/agenda)
  const handleViewChange = (newView) => {
    setCalendarView(newView);

    // Wait for the state to update and the calendar to render with new view
    setTimeout(() => {
      if (calendarRef.current) {
        const calendarApi = calendarRef.current.getApi();

        // Update the view
        calendarApi.changeView(viewModes[newView]);

        // Get the new date range and fetch events if needed
        const viewStart = calendarApi.view.currentStart;
        const viewEnd = calendarApi.view.currentEnd;

        // Only fetch new data if we have a significant change in date range
        if (selectedAccount?.id) {
          debouncedFetchEvents(selectedAccount.id);
        }
      }
    }, 10);
  };

  // Fetch calendar data whenever the selected account changes
  useEffect(() => {
    const fetchData = async () => {
      if (!user) {
        return;
      }
      if (selectedAccount && selectedAccount.id) {
        try {
          setLoading(true);
          setError(null);

          console.log(
            `Loading calendar data for account: ${selectedAccount.id}`
          );

          // Format dates for the date range
          const now = new Date();
          const startDate = new Date(now);
          startDate.setMonth(now.getMonth() - 1); // 1 month before

          const endDate = new Date(now);
          endDate.setMonth(now.getMonth() + 2); // 2 months after

          // Format for API: YYYY-MM-DD
          const formatApiDate = (date) => {
            return date.toISOString().split("T")[0];
          };

          const formattedStartDate = formatApiDate(startDate);
          const formattedEndDate = formatApiDate(endDate);

          // Use the calendar events endpoint with date range
          const endpoint = API_ENDPOINTS.CALENDAR_EVENTS(
            user.id,
            selectedAccount.id,
            formattedStartDate,
            formattedEndDate
          );

          console.log(`Fetching calendar data from: ${endpoint}`);

          const response = await axios.get(endpoint, {
            withCredentials: true,
            headers: {
              Accept: "application/json",
              "Content-Type": "application/json",
            },
          });

          console.log("Calendar API response:", response);

          let fetchedEvents = [];

          if (response.data && Array.isArray(response.data.events)) {
            fetchedEvents = response.data.events;
          } else if (Array.isArray(response.data)) {
            fetchedEvents = response.data;
          } else if (response.data && typeof response.data === "object") {
            // Check if any property contains an array that might be events
            for (const key in response.data) {
              if (Array.isArray(response.data[key])) {
                fetchedEvents = response.data[key];
                break;
              }
            }
          }

          // Format events for FullCalendar
          console.log("Raw events:", fetchedEvents);
          const formattedEvents = formatEvents(fetchedEvents);
          console.log("Formatted events:", formattedEvents);

          setEvents(formattedEvents);
        } catch (error) {
          console.error("Error fetching calendar events:", error);
          setError("Failed to load calendar events. Please try again later.");
        } finally {
          setLoading(false);
        }
      }
    };

    fetchData();
  }, [selectedAccount, user]); // Only re-run when the selected account changes

  // Add this function to handle sidebar state changes
  const handleSidebarStateChange = (collapsed) => {
    setIsSidebarCollapsed(collapsed);
  };

  return (
    <div className="flex h-screen bg-[#EEEEEE]">
      <Sidebar
        isOpen={isMobileMenuOpen}
        onClose={() => setIsMobileMenuOpen(false)}
        onCollapse={handleSidebarStateChange}
      />

      <div
        className={`flex-1 flex flex-col transition-all duration-300 ${
          isSidebarCollapsed ? "w-[95%] ml-[5%]" : "w-[82%] ml-[18%]"
        }`}
      >
        <Navbar
          onMenuClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          onAccountChange={handleAccountChange}
          accounts={accounts}
          selectedAccountId={selectedAccount?.id}
          isSidebarCollapsed={isSidebarCollapsed}
        />

        <div className="flex-1 p-2">
          <div className="bg-white rounded-lg shadow-md p-2 h-[calc(100vh-120px)] flex flex-col overflow-hidden">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-2 gap-2">
              <h2 className="text-xl font-bold text-[#1F9BFF]">Calendar</h2>

              <div className="flex flex-wrap gap-2">
                {accounts.length > 0 && (
                  <select
                    className="bg-white border border-gray-300 rounded-md px-3 py-1 text-sm"
                    value={selectedAccount?.id || ""}
                    onChange={(e) => {
                      const accountId = e.target.value;
                      if (accountId === "all") {
                        handleAccountChange({
                          id: "all",
                          email: "All Accounts",
                          provider: "all",
                        });
                      } else {
                        const account = accounts.find(
                          (acc) => acc.id === accountId
                        );
                        if (account) handleAccountChange(account);
                      }
                    }}
                  >
                    <option value="all">All Accounts</option>
                    {accounts.map((account) => (
                      <option key={account.id} value={account.id}>
                        {account.email}
                      </option>
                    ))}
                  </select>
                )}

                <button
                  onClick={openExternalCalendar}
                  className="bg-blue-500 hover:bg-blue-600 text-white px-3 py-1 rounded-md text-sm transition duration-200"
                >
                  Open in{" "}
                  {viewingAllAccounts
                    ? "Browser"
                    : selectedAccount?.provider === "outlook"
                    ? "Outlook"
                    : "Google"}
                </button>
              </div>
            </div>

            <div className="flex-1 overflow-hidden">
              {renderCalendarContent()}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Calendar;
